import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PauseIcon, PlayIcon, RotateCcwIcon } from 'lucide-react';

/** Approved v5 arrival footage (silent). Speed is baked into the files — never altered here. */
const DESKTOP_VIDEO = 'https://cdn.magicpatterns.com/uploads/feitZZSmDmi4k4aH4iL38i/black-comet-arrival-web-v5.mp4';
const MOBILE_VIDEO = 'https://cdn.magicpatterns.com/uploads/8TuBjawMT64HSReiWSXNRf/black-comet-hero-mobile-v5.mp4';
const DESKTOP_POSTER = 'https://cdn.magicpatterns.com/uploads/cACJ5jgqUkZqCfR5RZzU2M/black-comet-hero-poster-v5.png';
const MOBILE_POSTER = 'https://cdn.magicpatterns.com/uploads/cCGyDjxcDbGVY7nEgHYxN9/black-comet-hero-mobile-poster-v5.png';
const MOBILE_QUERY = '(max-width: 767px)';
const VISIBILITY_THRESHOLD = 0.25;

/** Measured warm cream ground of the v3 scene, used for the surrounding canvas. */
const SCENE_CANVAS = '#FDF6E1';

/** Consistent treatment applied to canvas and media so no boundary shows. */
const MEDIA_FILTER = 'saturate(0.88) contrast(1.05)';

/**
 * Feather the sides (~6%) and the top (~9%) of the scaled media, intersected
 * so both apply at once. The bottom stays fully opaque — paws and ground
 * must remain solid.
 */
const FEATHER_MASK = 'linear-gradient(to right, transparent 0%, #000 6%, #000 94%, transparent 100%), linear-gradient(to bottom, transparent 0%, #000 9%, #000 100%)';
const FEATHER: React.CSSProperties = {
  maskImage: FEATHER_MASK,
  WebkitMaskImage: FEATHER_MASK,
  maskComposite: 'intersect',
  WebkitMaskComposite: 'source-in',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskSize: '100% 100%',
  WebkitMaskSize: '100% 100%'
};
type Status = 'idle' | 'playing' | 'paused' | 'ended' | 'failed';
function matches(query: string): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(query).matches;
}
function prefersSaveData(): boolean {
  if (typeof navigator === 'undefined') return false;
  const connection = (navigator as Navigator & {
    connection?: {
      saveData?: boolean;
    };
  }).connection;
  return Boolean(connection?.saveData);
}
export function HeroBackgroundVideo() {
  const layerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const userPausedRef = useRef(false);
  const visibleRef = useRef(true);
  const [isMobile, setIsMobile] = useState<boolean>(() => matches(MOBILE_QUERY));
  const [staticOnly, setStaticOnly] = useState<boolean>(() => matches('(prefers-reduced-motion: reduce)') || prefersSaveData());
  const [status, setStatus] = useState<Status>('idle');
  const poster = isMobile ? MOBILE_POSTER : DESKTOP_POSTER;
  const source = isMobile ? MOBILE_VIDEO : DESKTOP_VIDEO;

  // Keep the breakpoint source and preference in sync, including on rotation.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mobileQuery = window.matchMedia(MOBILE_QUERY);
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onMobileChange = () => setIsMobile(mobileQuery.matches);
    const onMotionChange = () => setStaticOnly(motionQuery.matches || prefersSaveData());
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', onMobileChange);
      motionQuery.addEventListener('change', onMotionChange);
    } else {
      mobileQuery.addListener(onMobileChange);
      motionQuery.addListener(onMotionChange);
    }
    return () => {
      if (mobileQuery.removeEventListener) {
        mobileQuery.removeEventListener('change', onMobileChange);
        motionQuery.removeEventListener('change', onMotionChange);
      } else {
        mobileQuery.removeListener(onMobileChange);
        motionQuery.removeListener(onMotionChange);
      }
    };
  }, []);
  const attemptPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || staticOnly) return;
    if (userPausedRef.current || video.ended) return;
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.then(() => setStatus('playing')).catch(() => setStatus(current => current === 'ended' ? current : 'idle'));
    }
  }, [staticOnly]);

  // Autoplay as soon as the hero is in view; pause when it leaves.
  useEffect(() => {
    const node = layerRef.current;
    if (!node || staticOnly) return;
    if (typeof IntersectionObserver === 'undefined') {
      attemptPlay();
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[entries.length - 1];
      const visible = entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD;
      visibleRef.current = visible;
      const video = videoRef.current;
      if (!video) return;
      if (visible) {
        attemptPlay();
      } else if (!video.paused) {
        video.pause();
        setStatus(current => current === 'ended' ? current : 'paused');
      }
    }, {
      threshold: [0, VISIBILITY_THRESHOLD, 0.6]
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [staticOnly, attemptPlay, source]);
  useEffect(() => {
    if (staticOnly) return;
    const onVisibilityChange = () => {
      const video = videoRef.current;
      if (!video) return;
      if (document.hidden) {
        if (!video.paused) {
          video.pause();
          setStatus(current => current === 'ended' ? current : 'paused');
        }
      } else if (visibleRef.current) {
        attemptPlay();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [staticOnly, attemptPlay]);
  const handleToggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (status === 'ended') {
      userPausedRef.current = false;
      video.currentTime = 0;
      void video.play().then(() => setStatus('playing')).catch(() => setStatus('idle'));
      return;
    }
    if (!video.paused) {
      userPausedRef.current = true;
      video.pause();
      setStatus('paused');
      return;
    }
    userPausedRef.current = false;
    void video.play().then(() => setStatus('playing')).catch(() => setStatus('idle'));
  };
  const controlLabel = status === 'ended' ? 'Replay background animation' : status === 'playing' ? 'Pause background animation' : status === 'paused' ? 'Resume background animation' : 'Play background animation';
  const ControlIcon = status === 'playing' ? PauseIcon : status === 'ended' ? RotateCcwIcon : PlayIcon;
  return <>
      <div ref={layerRef} className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-64" data-mp="19ecqtjqwrrdg">
        {/* Canvas matched to the scene's warm cream ground so the smaller
            media layer has no visible rectangle or letterbox bars. */}
        <div className="absolute inset-0" style={{
        backgroundColor: SCENE_CANVAS,
        filter: MEDIA_FILTER
      }} data-id="element-65" data-mp="1nbj6h5gc59yg" />

        {/* Scene layer: 80% scale on mobile, 75% on desktop, anchored near the
            bottom and nudged right on desktop for breathing room. */}
        <div className="absolute bottom-0 left-1/2 h-[84%] w-full -translate-x-1/2 md:left-auto md:right-[2%] md:h-[75%] md:w-[75%] md:translate-x-0" style={FEATHER} data-id="element-66" data-mp="2yt5ag00patlo">
          <img src={poster} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-bottom" style={{
          filter: MEDIA_FILTER
        }} data-id="element-67" data-mp="3rht1ydc1mf7o" />

          {!staticOnly && status !== 'failed' ? <video ref={videoRef} key={source} src={source} poster={poster} muted autoPlay playsInline preload="auto" aria-hidden="true" tabIndex={-1} onPlay={() => setStatus('playing')} onPause={() => {
          const video = videoRef.current;
          if (video && !video.ended) setStatus('paused');
        }} onEnded={() => setStatus('ended')} onError={() => setStatus('failed')} className="absolute inset-0 h-full w-full object-cover object-bottom" style={{
          filter: MEDIA_FILTER
        }} data-id="element-68" data-mp="3hpwq7zh1aqkz" /> : null}
        </div>

        {/* Ink overlay: strongest behind the copy, light over the animation. */}
        <div className="absolute inset-0 md:hidden" style={{
        background: 'linear-gradient(to bottom, rgba(21,20,27,0.9) 0%, rgba(21,20,27,0.86) 34%, rgba(21,20,27,0.62) 55%, rgba(21,20,27,0.3) 74%, rgba(21,20,27,0.42) 100%)'
      }} data-id="element-69" data-mp="5d60611vrejf" />
        <div className="absolute inset-0 hidden md:block" style={{
        background: 'linear-gradient(100deg, rgba(21,20,27,0.88) 0%, rgba(21,20,27,0.82) 34%, rgba(21,20,27,0.58) 58%, rgba(21,20,27,0.34) 80%, rgba(21,20,27,0.3) 100%)'
      }} data-id="element-70" data-mp="2c3vmuwfv43b7" />
        <div className="absolute inset-0 hidden md:block" style={{
        background: 'linear-gradient(to bottom, rgba(21,20,27,0.42) 0%, rgba(21,20,27,0) 28%, rgba(21,20,27,0) 72%, rgba(21,20,27,0.46) 100%)'
      }} data-id="element-71" data-mp="24kcq7ndp4bk5" />
      </div>

      {!staticOnly && status !== 'failed' ? <button type="button" onClick={handleToggle} title={controlLabel} className="absolute bottom-4 right-4 z-20 inline-flex h-11 w-11 items-center justify-center rounded-[2px] border border-cream/30 bg-ink/60 text-cream transition-colors duration-150 ease-out hover:border-cream/60 hover:bg-ink/80 md:bottom-6 md:right-6" data-id="element-72" data-mp="1puf6bjmsu80r">
          <ControlIcon className="h-4 w-4" aria-hidden="true" data-id="element-73" data-mp="174p9ys3e1e6q" />
          <span className="sr-only" data-id="element-74" data-mp="127feovtbi8az">{controlLabel}</span>
        </button> : null}
    </>;
}